<script setup>
  import BaseUserAvatar from '@/components/BaseUserAvatar.vue';
  import WorkspaceLogo from '@/components/WorkspaceLogo.vue';
  import addTeamMemberWorkspacesDialog from '@/pages/settings/account/components/addTeamMemberWorkspaceModal.vue';
  import financialAccessMembersDialog from '@/pages/settings/account/components/financialAccessMemberModal.vue';
  import inviteTeamMemberDialog from '@/pages/settings/account/components/inviteTeamMembersModal.vue';
  import router from '@/router/index.js';
  import { REVOKE_ALL_USER_ROLES_IN_ACCOUNT, UPDATE_USER_ROLE_IN_ACCOUNT } from '@/services/account/mutations/account';
  import { GET_ACCOUNT_TEAM_MEMBERS_QUERY } from '@/services/account/queries/account';
  import { GET_ENUMS_QUERY } from '@/services/common/queries.js';
  import { API } from '@/services/index';
  import { REVOKE_WORKSPACE_USER_ROLE } from '@/services/workspace/mutations/workspace';
  import { usePermissionsStore } from '@/stores/permissions.store.js';
  import { useSettingsStore } from '@/stores/settings.store.js';
  import { useUserStore } from '@/stores/user.store.js';
  import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
  import { format } from 'date-fns';
  import { ref } from 'vue';

  const queryClient = useQueryClient();
  const permissions = usePermissionsStore();
  const isPermissionsLoading = computed(() => useUserStore().isPermissionsLoading);
  const currentUser = computed(() => useUserStore().currentUser);
  const activeAccount = computed(() => useSettingsStore().activeAccount);
  const workspaces = computed(() => useSettingsStore().workspaces);
  const teamMembers = ref([]);
  const searchedTeamMember = ref('');
  const isUpdatingMemberWorkspaces = ref(false);
  const removeMemberConfirmationDialog = ref({
    isOpen: false,
    user: null,
    isRemoving: false,
  });

  const headers = [
    { title: 'Team Members', key: 'user', width: '350' },
    { title: '', key: 'workspaces', width: '1200px' },
    { title: 'Invite Link', key: 'inviteLink', width: '200' },
    { title: 'Last Login', key: 'lastLogin', width: '200' },
    { title: 'Verified', key: 'verified', width: '100' },
  ];

  const filteredTeamMembers = computed(() => {
    const mappedTeamMembers = [...teamMembers.value];
    return mappedTeamMembers.filter((data) => {
      if (searchedTeamMember.value === '' || searchedTeamMember.value === null || searchedTeamMember.value.length === 0) {
        return teamMembers.value;
      }

      if (data.user?.first_name) {
        return data.user.first_name.toLowerCase().includes(searchedTeamMember.value.toLowerCase());
      }

      if (data.user?.email) {
        return data.user.email.toLowerCase().includes(searchedTeamMember.value.toLowerCase());
      }
      return teamMembers.value;
    });
  });

  watch(activeAccount, async (oldAccount, newAccount) => {
    await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
  });

  const {
    isLoading,
    isPending,
    isFetching,
    error,
    data: teamMemberData,
  } = useQuery({
    queryKey: ['loadAccountTeamMembers'],
    queryFn: async () => {
      const payload = {
        account_id: Number(activeAccount.value.id),
      };
      const res = await API.sendQuery('/graphql/account', GET_ACCOUNT_TEAM_MEMBERS_QUERY, payload);
      if (res.data.data.teamMembers) {
        teamMembers.value = res.data.data.teamMembers
          .filter((member) => {
            if (member?.user && member?.workspaces) {
              return member;
            }
          })
          .map((member) => {
            // let selectedWorkspaces = [];
            // if (member?.workspaces) {
            //   selectedWorkspaces = member.workspaces.map((workspace) => {
            //     if (workspace.workspace) {
            //       return workspace.workspace;
            //     }
            //   });
            // }

            // remapped role names
            if (member.role.name === 'White_Glove') {
              member.role = {
                ...member.role,
                name: 'White Glove',
              };
            } else if (member.role.name === 'Account_Team_Manager') {
              member.role = {
                ...member.role,
                name: 'Account Team Manager',
              };
            }

            return {
              ...member,
              isUpdatingMemberRole: false,
              editRoles: false,
              editWorkspaces: false,
              showMoreWorkspaces: false,
              selectedWorkspaces: [],
              isRemovingWorkspace: {
                workspace_id: null,
                isLoading: false,
              },
              removeMember: false,
            };
          });

        return res.data.data.teamMembers;
      }
      //
      if (res.data.errors[0].message === 'Unauthorized') {
        teamMembers.value = [];
        return [];
      }
    },
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingAccountRoles,
    isPending: isPendingAccountRoles,
    isFetching: isFetchingAccountRoles,
    data: accountRoles,
  } = useQuery({
    queryKey: ['loadAccountRoles'],
    queryFn: async () => {
      const payload = {
        name: 'ACLRoleEnum',
      };
      const res = await API.sendQuery('/graphql', GET_ENUMS_QUERY, payload);
      if (res.data.data.getEnums) {
        const visibleAccountRoleTypes = ['Admin', 'Editor', 'Client', 'Watcher'];
        return res.data.data.getEnums
          .filter((role) => {
            return visibleAccountRoleTypes.includes(role);
          })
          .map((role) => {
            let name = role.replaceAll('_', ' ');
            return {
              id: role,
              name: name,
            };
          });
      }
    },
    refetchOnWindowFocus: false,
  });

  const updateUserRole = useMutation({
    mutationFn: async (payload) => {
      return await API.sendQuery('/graphql/account', UPDATE_USER_ROLE_IN_ACCOUNT, payload);
    },
    onSuccess: async (data, payload) => {
      teamMembers.value[payload.index].isUpdatingMemberRole = false;

      if (data.data.data.updateUserRole) {
      }

      if (data.data.error) {
        console.log(data.data.error);
      }
    },
    onError: (error) => {
      console.log('error', error);
      const foundMemberIndex = teamMemberData.value.indexOf(member);
      teamMemberData.value[foundMemberIndex].isUpdatingMemberRole = false;
    },
  });

  const updateMemberRolesHandler = async (member, index) => {
    teamMembers.value[index].isUpdatingMemberRole = true;

    const payload = {
      account_id: Number(activeAccount.value.id),
      user_id: Number(member.user.id),
      role: member.role.name,
      member: member,
      index: index,
    };

    await updateUserRole.mutateAsync(payload);
  };

  // const addUserWorkspace = useMutation({
  //   mutationFn: async (payload) => {
  //     return await API.sendQuery('/graphql/workspace', GRANT_INVITE_WORKSPACE_USER_ROLE, payload);
  //   },
  //   onSuccess: async (data) => {
  //     if (data.data.data.grantUserRole) {
  //       await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
  //     }
  //
  //     if (data.data.error) {
  //       console.log(data.data.error);
  //     }
  //   },
  //   onError: (error) => {
  //     console.log('error', error);
  //   }
  // });
  //
  // const updateMemberWorkspacesHandle = async (member) => {
  //   isUpdatingMemberWorkspaces.value = true;
  //   const payload = {
  //     account_id: Number(activeAccount.value.id),
  //     user_id: Number(member.user.id),
  //     workspace_id: Number(workspace.id),
  //     member: member
  //   };
  //   await addUserWorkspace.mutateAsync(payload);
  // };

  const removeMemberFromAccount = useMutation({
    mutationFn: async (payload) => {
      return await API.sendQuery('/graphql/account', REVOKE_ALL_USER_ROLES_IN_ACCOUNT, payload);
    },
    onSuccess: async (data) => {
      if (data.data.data.revokeAllUserRoles) {
        removeMemberConfirmationDialog.value = {
          isRemoving: false,
          isOpen: false,
          user: null,
        };
        await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
      }

      if (data.data.error) {
        console.log(data.data.error);
        removeMemberConfirmationDialog.value.isRemoving = false;
      }
    },
    onError: (error) => {
      console.log('error', error);
      removeMemberConfirmationDialog.value.isRemoving = false;
    },
  });

  const revokeWorkspaceUserRole = useMutation({
    mutationFn: async (payload) => {
      return await API.sendQuery('/graphql/workspace', REVOKE_WORKSPACE_USER_ROLE, payload);
    },
    onSuccess: async (data, payload) => {
      if (data.data.data.revokeUserRole) {
        await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
        // console.log('teamMembers.value[payload.index]', teamMembers.value[payload.memberIndex]);
      } else {
        teamMembers.value[payload.index].isRemovingWorkspace = {
          workspace_id: null,
          isLoading: false,
        };
      }

      if (data.data.error) {
        console.log(data.data.error);
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  const revokeAccountUserRoleHandler = async (index, member, workspaceID, workspaceRole) => {
    if (confirm('Are you sure you want to remove this user from this workspace?')) {
      teamMembers.value[index].isRemovingWorkspace = {
        workspace_id: workspaceID,
        isLoading: true,
      };
      const payload = {
        workspace_id: Number(workspaceID),
        user_id: Number(member.user.id),
        role: workspaceRole,
        member: member,
        memberIndex: index,
      };
      await revokeWorkspaceUserRole.mutateAsync(payload);
    }
  };

  const isOwnerAndAuthUser = (member) => {
    return currentUser.value.id === member.user.id;
  };

  const ManageWorkspace = (workspace) => {
    useSettingsStore().setActiveWorkspace(workspace);
    router.push({ name: 'Workspace Details' });
  };

  const openRemoveMemberConfirmationDialog = (user) => {
    removeMemberConfirmationDialog.value = {
      isOpen: true,
      user: user,
      isRemoving: false,
    };
  };

  const removeMemberFromAccountHandle = async () => {
    removeMemberConfirmationDialog.value.isRemoving = true;
    const payload = {
      account_id: activeAccount.value.id,
      user_id: removeMemberConfirmationDialog.value.user.id,
    };

    await removeMemberFromAccount.mutateAsync(payload);
  };

  const copyToClipboard = (e, url) => {
    e.preventDefault();
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast('Copied to clipboard', {
          autoClose: 1000,
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      })
      .catch((err) => {
        console.error('Failed to copy text:', err);
      });
    return false;
  };
</script>

<template>
  <div class="tw-max-w-full lg:tw-max-w-[750px]">
    <div class="tw-mb-5 tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-between lg:tw-flex-row lg:tw-space-x-10">
      <div class="tw-flex tw-w-full tw-items-center">
        <v-text-field
          v-model="searchedTeamMember"
          hide-details
          density="compact"
          variant="solo"
          placeholder="Search for a team member"
          append-inner-icon="mdi-magnify"
          clearable
          height="37"
          :disabled="isPermissionsLoading || teamMembers.length === 0"
        ></v-text-field>
      </div>
      <div class="tw-mt-5 tw-flex tw-w-full tw-justify-start tw-space-x-2 lg:tw-mt-0 lg:tw-justify-end">
        <inviteTeamMemberDialog v-if="permissions.canAddAccountTeamMember()" :accountRoles="accountRoles" :is-disabled="isLoading" />
        <financialAccessMembersDialog v-if="permissions.canGrantAccountantAccess()" :is-disabled="isLoading" :team-members="teamMembers" />
      </div>
    </div>
  </div>
  <div class="tw-mb-[4rem] tw-h-[600px]">
    <v-data-table-virtual
      :loading="isLoading || isFetching"
      :headers="headers"
      :items="filteredTeamMembers"
      :fixed-header="true"
      item-key="user"
      loading-text="Loading Team Members..."
      :sticky="true"
      :disable-sort="teamMembers.length === 0"
      items-per-page="-1"
      hide-default-footer
      height="600"
      density="compact"
    >
      <template v-slot:item="{ item, index }">
        <tr v-if="item.user">
          <td>
            <div class="tw-my-6 tw-flex tw-items-start tw-space-x-3">
              <BaseUserAvatar class="tw-mr-2" :text-styles="`cursor: pointer;font-size: 1.688rem; line-height: 64px;`" :size="50" :user="item.user" />
              <div class="tw-flex tw-flex-col tw-space-y-1">
                <template v-if="item.user.first_name || (item.user.username && item.user.username.length > 0)">
                  <div class="tw-text tw-flex tw-space-x-2 tw-font-semibold">
                    <span>{{ item.user.username.length > 0 ? item.user.username : `${item.user.first_name} ${item.user.last_name}` }}</span>
                    <span v-if="isOwnerAndAuthUser(item)" class="tw-text-[0.8rem] tw-font-bold tw-italic tw-text-[#ff5b9f]">(You)</span>
                  </div>
                </template>
                <div class="tw-text-sm tw-font-medium tw-text-[#979797]">{{ item.user.email }}</div>
                <div v-if="item.role.name !== 'Owner' && !isOwnerAndAuthUser(item)">
                  <v-select
                    class="user-role-select"
                    v-model="item.role.name"
                    item-title="name"
                    item-value="name"
                    variant="solo"
                    density="compact"
                    :disabled="isUpdatingMemberWorkspaces || item.isUpdatingMemberRole"
                    :loading="item.isUpdatingMemberRole"
                    :items="accountRoles"
                    @update:modelValue="updateMemberRolesHandler(item, index)"
                    min-width="150"
                    width="150"
                    hide-details
                  >
                    <template v-slot:append-item>
                      <v-divider class="mb-2"></v-divider>
                      <v-list-item style="color: red" title="Remove" @click="openRemoveMemberConfirmationDialog(item.user)"> </v-list-item>
                    </template>
                  </v-select>

                  <v-dialog
                    v-if="removeMemberConfirmationDialog.user?.id === item.user.id"
                    v-model="removeMemberConfirmationDialog.isOpen"
                    @update:modelValue="removeMemberConfirmationDialog.isOpen"
                    width="auto"
                    attach="#app"
                    :persistent="removeMemberConfirmationDialog.isRemoving"
                  >
                    <v-card class="tw-rounded-xl" max-width="497" width="497" :loading="removeMemberConfirmationDialog.isRemoving">
                      <div class="tw-px-5 tw-pb-5">
                        <h2 class="tw-my-3 tw-text-center tw-text-[0.79rem] tw-text-xl tw-font-bold tw-capitalize tw-text-primary-blue">Remove Team Member</h2>
                        <div class="tw-rounded-xl tw-p-3">
                          <div class="tw-flex tw-w-full tw-items-center">
                            <BaseUserAvatar style="margin: 0 auto" :text-styles="`cursor: pointer;font-size: 1.688rem; line-height: 64px;`" :size="75" :user="item.user" />
                          </div>
                          <div class="tw-my-5 tw-text-center tw-text-[0.87rem] tw-font-semibold">
                            Are you sure you want to remove your team member's account? This will also remove them from all workspaces.
                          </div>
                          <div class="tw-mt-7 tw-flex tw-items-center tw-justify-between">
                            <v-btn :disabled="removeMemberConfirmationDialog.isRemoving" class="tw-capitalize" @click="removeMemberConfirmationDialog.isOpen = false"> Cancel </v-btn>
                            <v-btn
                              :loading="removeMemberConfirmationDialog.isRemoving"
                              :disabled="removeMemberConfirmationDialog.isRemoving"
                              class="tw-capitalize"
                              color="red"
                              @click="removeMemberFromAccountHandle"
                            >
                              Remove
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </v-card>
                  </v-dialog>
                </div>
                <div v-if="item.role.name === 'Owner' || isOwnerAndAuthUser(item)">
                  <div class="tw-text tw-font-semibold" :class="item.role.name === 'Owner' ? 'tw-text-[#e37f06]' : 'tw-text-primary-blue'">
                    {{ item.role.name }}
                  </div>
                </div>
                <!-- <div class="tw-whitespace-nowrap tw-text-left tw-text-xs tw-font-medium">
                  <v-btn
                    :icon="item.invite_url ? 'mdi-link-variant' : 'mdi-link-variant-off'"
                    variant="text"
                    @click="item.invite_url ? copyToClipboard($event, item.invite_url) : ''"
                    class="tw-mb-0 tw-cursor-pointer tw-text-secondary-blue"
                    :disabled="!item.invite_url"
                  ></v-btn>
                  <v-tooltip attach="#app" activator="parent" location="bottom" content-class="tw-text-[0.7rem] tw-bg-black tw-font-semibold">
                    <div class="tw-flex tw-space-x-2">
                      <div>{{ item.invite_url ? 'Copy invite link' : 'No link generated' }}</div>
                    </div>
                  </v-tooltip>
                </div> -->
              </div>
            </div>
          </td>
          <td>
            <v-table class="inner-table tw-my-4">
              <tr>
                <th class="tw-w-[1000px] text-left">Workspaces</th>
                <th class="tw-w-[303px] text-left"></th>
                <th class="tw-w-[303px] text-left">Invited By</th>
                <th class="tw-w-[303px] text-left">Invited</th>
                <th class="tw-w-[303px] text-left"></th>
              </tr>
              <tbody>
                <template v-if="item.workspaces[0].applies_to_all">
                  <tr>
                    <td>
                      <div class="tw-flex tw-items-center tw-space-x-2">
                        <v-btn width="43" height="43" size="x-small" density="compact" color="primary-blue">
                          <v-icon class="tw-text-[1.8rem]" icon="mdi-earth"></v-icon>
                        </v-btn>
                        <div class="tw-font-semibold">ALL</div>
                      </div>
                    </td>
                    <td>
                      <div class="invited-by-avatar" v-if="item.workspaces[0].invited_by">
                        <BaseUserAvatar class="tw-mb-1" :size="40" :user="item.workspaces[0].invited_by" />
                        <v-tooltip attach="#app" activator="parent" location="start">
                          {{ item.workspaces[0].invited_by.username || item.workspaces[0].invited_by.first_name }}
                        </v-tooltip>
                      </div>
                    </td>
                    <td>
                      <div class="tw-font-medium">
                        {{ item.workspaces[0]?.invited_date ? format(item.workspaces[0].invited_date, 'PPP') : '' }}
                      </div>
                    </td>
                    <td>
                      <v-hover v-slot="{ isHovering, props }" v-if="item.role.name !== 'Owner' && item.role.name !== 'Admin'">
                        <v-btn
                          v-bind="props"
                          :color="isHovering ? 'red' : 'blue-grey-lighten-4'"
                          variant="text"
                          density="compact"
                          icon="mdi-delete"
                          :loading="item.isRemovingWorkspace.isLoading"
                          :disabled="item.isRemovingWorkspace.isLoading"
                          @click="() => console.log('Removing user from this account.')"
                        ></v-btn>
                        <v-tooltip attach="#app" activator="parent" location="start" content-class="tw-text-[0.7rem] tw-bg-black tw-font-semibold"> Remove From All Workspaces </v-tooltip>
                      </v-hover>
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <template v-for="(workspace, workspaceIndex) in item.workspaces" :key="workspace.id">
                    <tr v-if="!item.showMoreWorkspaces ? workspaceIndex <= 2 : workspaceIndex >= 0">
                      <td>
                        <div class="tw-flex tw-items-center tw-space-x-2">
                          <div class="tw-group tw-flex tw-cursor-pointer tw-items-center tw-space-x-2 tw-space-y-2" @click="ManageWorkspace(workspace.workspace)">
                            <div>
                              <!--                              <v-img-->
                              <!--                                class="tw-h-[35px] tw-w-[67px]"-->
                              <!--                                :src="workspace.workspace?.image ?? imagePlaceholder"-->
                              <!--                                :alt="`${workspace.workspace?.name} workspace logo`"-->
                              <!--                              ></v-img>-->
                              <WorkspaceLogo :workspace="workspace.workspace" size="1.7rem" />
                            </div>
                            <div>
                              <div class="tw-flex tw-flex-col">
                                <div class="tw-font-semibold group-hover:tw-text-primary-blue">
                                  {{ workspace.workspace.name }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <v-chip v-if="workspace.features.length > 0" color="#0a9700" density="comfortable" size="small" prepend-icon="mdi-currency-usd"> Accountant </v-chip>
                      </td>
                      <td>
                        <template v-if="workspace.invited_by">
                          <div class="invited-by-avatar">
                            <BaseUserAvatar class="tw-mb-1" :size="40" :user="workspace.invited_by" />
                            <v-tooltip attach="#app" activator="parent" location="start" content-class="tw-text-[0.7rem] tw-bg-black tw-font-semibold">
                              {{ workspace.invited_by.username || workspace.invited_by.first_name }}
                            </v-tooltip>
                          </div>
                        </template>
                      </td>
                      <td>
                        <div class="tw-font-medium">
                          {{ workspace && workspace.invited_date ? format(workspace.invited_date, 'PPP') : '' }}
                        </div>
                      </td>
                      <td>
                        <v-hover v-slot="{ isHovering, props }" v-if="item.user.id !== currentUser.id">
                          <v-btn
                            v-bind="props"
                            :color="isHovering ? 'red' : 'blue-grey-lighten-4'"
                            variant="text"
                            density="compact"
                            icon="mdi-delete"
                            :loading="item.isRemovingWorkspace.workspace_id === workspace.workspace.id && item.isRemovingWorkspace.isLoading"
                            :disabled="item.isRemovingWorkspace.workspace_id === workspace.workspace.id && item.isRemovingWorkspace.isLoading"
                            @click="
                              item.workspaces.length === 1
                                ? openRemoveMemberConfirmationDialog(item.user)
                                : revokeAccountUserRoleHandler(index, item, workspace.workspace.id, workspace.role.name)
                            "
                          ></v-btn>
                          <v-tooltip attach="#app" activator="parent" location="start" content-class="tw-text-[0.7rem] tw-bg-black tw-font-semibold"> Remove Workspace </v-tooltip>
                        </v-hover>
                      </td>
                    </tr>
                  </template>
                  <div class="tw-mt-2">
                    <addTeamMemberWorkspacesDialog :account-roles="accountRoles" :member="item" :workspaces="workspaces" />
                    <v-btn
                      density="compact"
                      variant="text"
                      class="tw-mt-2 tw-text-[0.79rem] tw-font-bold tw-capitalize"
                      style="padding-left: 0; padding-right: 0"
                      color="primary-blue"
                      @click="item.showMoreWorkspaces = !item.showMoreWorkspaces"
                      v-if="item.workspaces.length > 3"
                    >
                      {{ item.showMoreWorkspaces ? 'See less' : 'See All' }}
                    </v-btn>
                  </div>
                </template>
              </tbody>
            </v-table>
          </td>
          <td>
            <div class="tw-font-medium tw-w-[45px]">
              <v-btn
                :icon="item.invite_url ? 'mdi-link-variant' : 'mdi-link-variant-off'"
                variant="text"
                @click="item.invite_url ? copyToClipboard($event, item.invite_url) : ''"
                class="tw-mb-0 tw-cursor-pointer tw-text-secondary-blue"
                :disabled="!item.invite_url"
              ></v-btn>
              <v-tooltip attach="#app" activator="parent" location="bottom" content-class="tw-text-[0.7rem] tw-bg-black tw-font-semibold">
                <div class="tw-flex tw-space-x-2">
                  <div>{{ item.invite_url ? 'Copy invite link' : 'No link generated' }}</div>
                </div>
              </v-tooltip>
            </div>
          </td>
          <td>
            <div>
              <div class="tw-font-medium">
                {{ item.user.last_login ? format(item.user.last_login, 'PPP') : '' }}
              </div>
            </div>
          </td>
          <td>
            <div class="tw-font-medium">
              <v-chip :model-value="item.user.verified" class="ma-2" color="teal" prepend-icon="mdi-checkbox-marked-circle" density="comfortable" size="small">
                {{ item.user.verified ? 'Verified' : 'Not Verified' }}
              </v-chip>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table-virtual>
  </div>
</template>

<style scoped lang="css">
  #partnrup .user-role-select.v-select .v-select__selection-text {
    @apply tw-text-[0.8rem] tw-font-semibold;
  }

  #partnrup .v-table.inner-table td {
    @apply tw-border-b-0 tw-pl-0;
  }
</style>
